// Timeline events for checkpoints, retries, quota and handoffs (contract
// docs/plans/2026-10-02-checkpoints-contract.md §6). Each is its own
// `run_event_type` value (migration 0021); `run_events.payload` is the schema
// below. The API's event-stream.service.ts maps them onto the `RunEvent`
// union (types/run.ts) for the SSE stream.

import { z } from 'zod'
import { RunnerFailureSchema } from './failure.js'
import { CheckpointTriggerSchema, RunCheckpointKindSchema } from './checkpoint.js'
import { HandoffReasonSchema, ResumeViaSchema } from './payload.js'

export const RUN_CHECKPOINT_EVENT_TYPES = [
  'runner_failure',
  'runner_retry',
  'checkpoint_saved',
  'quota_exhausted',
  'handed_off',
  'resumed',
] as const
export type RunCheckpointEventType = (typeof RUN_CHECKPOINT_EVENT_TYPES)[number]

/** Worker: a runner ended with a classified failure (every kind; before any retry/report). */
export const RunnerFailureEventPayloadSchema = z.object({
  failure: RunnerFailureSchema,
  /** The failure checkpoint written for it, if any (transient/rate_limit retries write none). */
  checkpointId: z.string().uuid().nullable(),
})
export type RunnerFailureEventPayload = z.infer<typeof RunnerFailureEventPayloadSchema>

/** Worker: restarting the runner in the same container after a transient/rate-limit failure. */
export const RunnerRetryEventPayloadSchema = z.object({
  kind: z.enum(['transient', 'rate_limit']),
  /** 1-based retry number. */
  attempt: z.number().int().min(1),
  maxAttempts: z.number().int().min(0),
  delayMs: z.number().int().nonnegative(),
  /** `session`: Claude `--resume` / Pi session file. `prompt`: a resume prompt (Codex, or no session). */
  resumeMode: z.enum(['session', 'prompt']),
  message: z.string(),
})
export type RunnerRetryEventPayload = z.infer<typeof RunnerRetryEventPayloadSchema>

/** Worker: a checkpoint was written (or skipped because the patch was too large). Unchanged periodic checkpoints are not written and not logged. */
export const CheckpointSavedEventPayloadSchema = z.object({
  checkpointId: z.string().uuid().nullable(),
  kind: RunCheckpointKindSchema,
  trigger: CheckpointTriggerSchema,
  patchBytes: z.number().int().nonnegative(),
  sessionBytes: z.number().int().nonnegative(),
  skipped: z.enum(['too_large']).optional(),
})
export type CheckpointSavedEventPayload = z.infer<typeof CheckpointSavedEventPayloadSchema>

/** API: a usage limit was hit on the run's connection, and what was decided. */
export const QuotaExhaustedEventPayloadSchema = z.object({
  connectionId: z.string().uuid().nullable(),
  provider: z.string().nullable(),
  exhaustedUntil: z.string().nullable(),
  decision: z.enum(['wait', 'handoff', 'blocked']),
  resumeAfter: z.string().nullable(),
  childRunId: z.string().uuid().nullable(),
  toAgentId: z.string().uuid().nullable(),
  message: z.string(),
})
export type QuotaExhaustedEventPayload = z.infer<typeof QuotaExhaustedEventPayloadSchema>

/** API, on the *old* run: its work went to `childRunId`. (The child's own timeline gets `resumed` with `via: 'handoff'`.) */
export const HandedOffEventPayloadSchema = z.object({
  childRunId: z.string().uuid(),
  fromAgentId: z.string().uuid(),
  toAgentId: z.string().uuid(),
  toAgentName: z.string(),
  reason: HandoffReasonSchema,
  checkpointId: z.string().uuid().nullable(),
  handoffNumber: z.number().int().min(1),
  /** The person who handed off manually; null for an automatic (quota) handoff. */
  actorId: z.string().nullable(),
})
export type HandedOffEventPayload = z.infer<typeof HandedOffEventPayloadSchema>

/** Worker: the run restored a checkpoint and its runner is about to start. */
export const ResumedEventPayloadSchema = z.object({
  checkpointId: z.string().uuid().nullable(),
  checkpointKind: RunCheckpointKindSchema.nullable(),
  via: z.union([ResumeViaSchema, z.literal('handoff')]),
  /** `session`: the runner's own transcript continues. `prompt`: resume prompt. `handoff_prompt`: buildHandoffPrompt. */
  mode: z.enum(['session', 'prompt', 'handoff_prompt']),
  /** Handoff only: the run whose work this is. */
  fromRunId: z.string().uuid().nullable(),
  patchRestored: z.boolean(),
})
export type ResumedEventPayload = z.infer<typeof ResumedEventPayloadSchema>

export const RUN_CHECKPOINT_EVENT_PAYLOAD_SCHEMAS = {
  runner_failure: RunnerFailureEventPayloadSchema,
  runner_retry: RunnerRetryEventPayloadSchema,
  checkpoint_saved: CheckpointSavedEventPayloadSchema,
  quota_exhausted: QuotaExhaustedEventPayloadSchema,
  handed_off: HandedOffEventPayloadSchema,
  resumed: ResumedEventPayloadSchema,
} as const satisfies Record<RunCheckpointEventType, z.ZodTypeAny>

export interface RunCheckpointEventPayloads {
  runner_failure: RunnerFailureEventPayload
  runner_retry: RunnerRetryEventPayload
  checkpoint_saved: CheckpointSavedEventPayload
  quota_exhausted: QuotaExhaustedEventPayload
  handed_off: HandedOffEventPayload
  resumed: ResumedEventPayload
}
